// TEN17 (docs/build-briefs/TEN17-brand-settings-and-theme.md; DEC-342 Q5) and TEN17-F5 (DEC-429): the browser side of
// applying a brand theme. A brand theme is not a stylesheet file: it is one style rule, written from a token map that
// `isSafeBrandTokens` has already confirmed holds only the allowed color and font custom properties, plus the font.
// Nothing here sets a layout property, a size or a fixed state color, and nothing here logs.
//
// THE FONT is loaded lazily, one brand font at a time. A free font links ONLY its own stylesheet
// (/design/themes/brand-fonts/<id>.css, self-hosted, never a CDN; a display face also links Instrument Sans, which
// its body text uses) and the browser fetches a face's file only when text uses it. An UPLOADED font is fetched by
// the page with its session token and registered from the bytes (`FontFace` over an ArrayBuffer), so it needs no
// URL the browser could fetch without a session and no change to the CSP `font-src 'self'`.

import { brandPreviewDeclarations, brandThemeCss, type BrandTokens } from "../../src/domain/brandTheme.ts";
import { catalogFont, uploadedFontIdOf } from "../../src/domain/brandFontCatalog.ts";

const STYLE_ID = "brand-theme-style";
const FONT_CSS_DIR = "/design/themes/brand-fonts/";

function linkFontStylesheet(fontId: string): void {
  const linkId = `brand-font-${fontId}`;
  if (document.getElementById(linkId) !== null) return;
  const link = document.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  link.href = `${FONT_CSS_DIR}${fontId}.css`;
  document.head.append(link);
}

/** Links the stylesheet of the free font `fontId` (and Instrument Sans for a display face). An uploaded font and the
 * system stack have no stylesheet. */
export function ensureBrandFont(fontId: string): void {
  const font = catalogFont(fontId);
  if (font === undefined) return;
  linkFontStylesheet(font.id);
  if (font.display && font.id !== "instrument-sans") linkFontStylesheet("instrument-sans");
}

const registeredUploads = new Set<string>();

/** Registers an uploaded font's bytes under `family` (once per family). Resolves true when the browser accepted the
 * font. The browser's own font sanitizer judges the bytes; a font it refuses resolves false and the fallback stack
 * shows. */
export async function registerUploadedFont(
  family: string,
  bytes: ArrayBuffer,
  isCurrent: () => boolean = () => true,
): Promise<boolean> {
  if (!isCurrent()) return false;
  if (registeredUploads.has(family)) return true;
  try {
    const face = new FontFace(family, bytes);
    await face.load();
    if (!isCurrent()) return false;
    document.fonts.add(face);
    registeredUploads.add(family);
    return true;
  } catch {
    return false;
  }
}

/** True when the font `fontId` is an uploaded one (`upload:<uuid>`). */
export function isUploadedFontId(fontId: string): boolean {
  return uploadedFontIdOf(fontId) !== null;
}

/** Applies a brand theme to the page: writes the one rule for `themeId`, links the font's stylesheet, and stamps
 * `data-theme` on the root so every selector keyed on the theme sees it. Throws when the id or tokens are unsafe. */
export function applyBrandTheme(themeId: string, tokens: BrandTokens, fontId: string): void {
  const css = brandThemeCss(themeId, tokens);
  ensureBrandFont(fontId);
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (style === null) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    document.head.append(style);
  }
  style.textContent = css;
  document.documentElement.dataset["theme"] = themeId;
}

/** Sets the preview tokens on one element (the live preview), by custom property, leaving the page untouched. */
export function applyPreviewTokens(target: HTMLElement, tokens: BrandTokens, fontId: string): void {
  ensureBrandFont(fontId);
  for (const [name, value] of brandPreviewDeclarations(tokens)) target.style.setProperty(name, value);
}
